/**
 * Diagnostic assessment, referring therapist's view (ADHD-31): request a
 * diagnostic assessment from a credentialed assessor and see the signed result.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import api from '../../services/api';

export const CONCLUSIONS = {
  adhd_combined: 'ADHD, combined presentation',
  adhd_predominantly_inattentive: 'ADHD, predominantly inattentive presentation',
  adhd_predominantly_hyperactive_impulsive: 'ADHD, predominantly hyperactive-impulsive presentation',
  other_specified_adhd: 'Other specified ADHD',
  not_adhd: 'ADHD not diagnosed',
  inconclusive: 'Inconclusive: further assessment needed'
};
export const STATUS = { requested: 'Requested', in_progress: 'In progress', signed: 'Signed', cancelled: 'Cancelled' };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

export default function DiagnosticRequestPanel({ clientId }) {
  const [list, setList] = useState(null);
  const [assessors, setAssessors] = useState([]);
  const [open, setOpen] = useState(false);
  const [assessorId, setAssessorId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [showReport, setShowReport] = useState(null);

  const load = useCallback(async () => {
    try {
      const [res, screens] = await Promise.all([
        api.get(`/diagnostic/client/${clientId}`),
        api.get(`/screening-outcomes/client/${clientId}`).catch(() => ({ data: { outcomes: [] } }))
      ]);
      if (res.data.enabled === false) { setList(null); return; }
      const assessments = res.data.assessments || [];
      // Only relevant on an ADHD pathway: show once the client has a screen or a request.
      setList(assessments.length || (screens.data.outcomes || []).length ? assessments : null);
    } catch (err) {
      if (err.response?.status === 403) setList(null);
      else setError('Could not load diagnostic assessments.');
    }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const openDialog = async () => {
    setOpen(true);
    try { setAssessors((await api.get('/diagnostic/assessors')).data.assessors || []); } catch (_) { setAssessors([]); }
  };

  const submit = async () => {
    setError(null);
    try {
      await api.post(`/diagnostic/client/${clientId}`, { assessorId, notes });
      setOpen(false); setAssessorId(''); setNotes('');
      load();
    } catch (err) { setError(err.response?.data?.error || 'Could not send the request.'); }
  };

  if (list === null) return null;
  const hasOpen = list.some(a => ['requested', 'in_progress'].includes(a.status));

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="diagnostic-request-panel">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Diagnostic assessment</Typography>
        <Box sx={{ flex: 1 }} />
        {!hasOpen && <Button size="small" variant="outlined" onClick={openDialog}>Request a diagnostic assessment</Button>}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        A credentialed clinician interviews the client and makes the diagnosis. The questionnaires and informant input on file go with the request.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {list.length === 0 && <Typography variant="body2">None requested.</Typography>}
      {list.map(a => (
        <Box key={a._id} sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2">Requested {fmtDate(a.createdAt)}</Typography>
            <Chip size="small" label={STATUS[a.status]} color={a.status === 'signed' ? 'success' : 'default'} variant="outlined" />
            {a.status === 'signed' && <Typography variant="body2" sx={{ fontWeight: 600 }}>{CONCLUSIONS[a.outcome?.conclusion]}</Typography>}
            {a.amendments?.length > 0 && <Chip size="small" label={`${a.amendments.length} amendment${a.amendments.length > 1 ? 's' : ''}`} variant="outlined" />}
            <Box sx={{ flex: 1 }} />
            {a.status === 'signed' && <Button size="small" onClick={() => setShowReport(a)}>Read the report</Button>}
            <Button size="small" component={RouterLink} to={`/therapist/diagnostic/${a._id}`}>Open</Button>
          </Box>
        </Box>
      ))}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request a diagnostic assessment</DialogTitle>
        <DialogContent>
          {assessors.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No verified assessors are available yet.</Alert>}
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="assessor-label">Assessor</InputLabel>
            <Select labelId="assessor-label" label="Assessor" value={assessorId} onChange={e => setAssessorId(e.target.value)}>
              {assessors.map(a => <MenuItem key={a.userId} value={a.userId}>{a.name}, {a.profession} ({a.jurisdiction})</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth multiline minRows={4} label="Reason for referral" value={notes} onChange={e => setNotes(e.target.value)}
            helperText="What prompted the referral and anything the assessor should know." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!assessorId} onClick={submit}>Send request</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!showReport} onClose={() => setShowReport(null)} maxWidth="md" fullWidth>
        <DialogTitle>Diagnostic assessment report</DialogTitle>
        <DialogContent>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14 }}>{showReport?.report?.text}</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setShowReport(null)}>Close</Button></DialogActions>
      </Dialog>
    </Paper>
  );
}
